import { CSVParseResult, ManualLead } from "@/types/manualLeadUpload";
import Papa, { LocalFile } from "papaparse";
import { z } from "zod";
import type { Dispatch, SetStateAction } from "react";
import { toast } from "sonner";
import { SupabaseClient, User } from "@supabase/supabase-js";
import { sendPreCallEmails } from "@/hooks/useEmailQueries";

export const INSTRUCTION_MIN_LENGTH = 10;
export const INSTRUCTION_MAX_LENGTH = 5000;
export const MAX_LEADS_PER_UPLOAD = 100;
export const MAX_FILE_SIZE_MB = 5;
export const ZAPIER_SLACK_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/7132591/2v9utnt/";

export type SingleLeadFormData = z.infer<typeof singleLeadSchema>;

export const singleLeadSchema = z.object({
  name: z.string().max(100, 'Name too long (max 100 chars)').optional().nullable(),
  phone_number: z.string()
      .min(1, "Phone number is required.") // Basic check for non-empty string
      .transform((val, ctx) => {
          const normalized = normalizeUSPhoneNumber(val);
          if (!normalized) {
              ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: "Invalid US phone number. Accepted formats include (XXX) XXX-XXXX, XXXXXXXXXX, 1XXXXXXXXXX, +1XXXXXXXXXX.",
              });
              return z.NEVER; // This ensures the transform fails
          }
          return normalized;
      }),
  email: z.string().email('Invalid email format').optional().nullable().or(z.literal('')),
  manual_call_instruction: z.string()
      .min(INSTRUCTION_MIN_LENGTH, `Please provide a clear instruction (min ${INSTRUCTION_MIN_LENGTH} characters).`)
      .max(INSTRUCTION_MAX_LENGTH, `Instruction too long (max ${INSTRUCTION_MAX_LENGTH} characters)`),
});


export function normalizeUSPhoneNumber(phoneNumber: string): string | null {
  if (!phoneNumber || typeof phoneNumber !== 'string') return null;
  const digits = phoneNumber.replace(/\D/g, ''); // Remove all non-digit characters
  // e.g., 3334445555 or (333) 444-5555 // Becomes +1XXXXXXXXXX
  if (digits.length === 10) return `+1${digits}`;
  // e.g., 13334445555 or +1 (333) 444-5555 // Becomes +1XXXXXXXXXX
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null; // Invalid or unrecognized US phone number format
}


export const parseCsvFile = (
  file: File, 
  setIsSubmitting: (isSubmitting: boolean) => void,
  setParseErrors: Dispatch<SetStateAction<string[]>>,
  setParsedLeads: (leads: ManualLead[]) => void,
  toast: any,
  isPreCallEmailsEnabled: boolean
) => {
  setIsSubmitting(true); 
  const currentFileName = file.name; 
  const parseToastId = toast.loading(`Parsing ${currentFileName}...`);

  try {
      Papa.parse<any>(file, { 
          header: true,
          skipEmptyLines: true,
          transformHeader: (header: string) => header.toLowerCase().replace(/\s+/g, '_'),
          complete: (results: CSVParseResult) => { 
              console.log("CSV Parsing Results:", results);
              const validationErrors: string[] = [];
              const validLeads: ManualLead[] = [];

              const headers = results.meta.fields?.map((h: string) => h.toLowerCase().replace(/\s+/g, '_'));
              
              // Enhanced column validation against template
              const requiredColumns = ['phone_number'];
              const optionalColumns = isPreCallEmailsEnabled ? ['name', 'email'] : ['name'];
              const expectedColumns = [...requiredColumns, ...optionalColumns];
              
              if (!headers || headers.length === 0) {
                  validationErrors.push("CSV format error: No headers found. Please ensure your CSV has column headers.");
                  setParseErrors(validationErrors);
                  setParsedLeads([]);
                  toast.dismiss(parseToastId);
                  toast.error(`The CSV file '${currentFileName}' has no headers. Please use our template format.`, { duration: 8000 });
                  setIsSubmitting(false);
                  return;
              }

              // Check for required columns
              const missingRequired = requiredColumns.filter(col => !headers.includes(col));
              if (missingRequired.length > 0) {
                  validationErrors.push(`CSV format error: Missing required column(s): ${missingRequired.join(', ')}. Please ensure your CSV has a 'phone_number' column.`);
              }

              // Check for unexpected columns
              const unexpectedColumns = headers.filter((col: string) => !expectedColumns.includes(col));
              if (unexpectedColumns.length > 0) {
                  const supportedColumns = isPreCallEmailsEnabled 
                      ? "'phone_number' (required), 'name' and 'email' (optional)"
                      : "'phone_number' (required) and 'name' (optional)";
                  validationErrors.push(`CSV format warning: Unexpected column(s) found: ${unexpectedColumns.join(', ')}. Only ${supportedColumns} columns are supported.`);
              }

              // Check for optional columns and provide feedback
              const hasNameColumn = headers.includes('name');
              const hasEmailColumn = headers.includes('email');
              if (!hasNameColumn) {
                  validationErrors.push("CSV format info: 'name' column is optional but recommended. Leads without names will still be processed.");
              }
              if (isPreCallEmailsEnabled && !hasEmailColumn) {
                  validationErrors.push("CSV format info: 'email' column is optional but recommended for pre-call notifications. Leads without emails will still be processed.");
              }

              // Only proceed with data processing if required columns are present
              if (missingRequired.length === 0) {
                  const leadsToProcess = results.data.slice(0, MAX_LEADS_PER_UPLOAD);
                  if (results.data.length > MAX_LEADS_PER_UPLOAD) {
                      validationErrors.push(`Warning: File contained ${results.data.length} leads, but only the first ${MAX_LEADS_PER_UPLOAD} were processed due to the upload limit.`);
                  }

                  leadsToProcess.forEach((row: any, index: number) => {
                      const lineNumber = index + 2; 
                      const lead: Partial<ManualLead> = {};

                      const phoneRaw = row.phone_number?.toString().trim();
                      if (!phoneRaw) {
                          validationErrors.push(`Row ${lineNumber}: Phone number is missing or empty.`);
                          return;
                      }
                      
                      const normalizedPhone = normalizeUSPhoneNumber(phoneRaw);
                      if (!normalizedPhone) {
                           validationErrors.push(`Row ${lineNumber}: Invalid US phone number for '${phoneRaw}'. Accepted formats include (XXX) XXX-XXXX, XXXXXXXXXX, 1XXXXXXXXXX, +1XXXXXXXXXX.`);
                           return;
                      }
                      lead.phone_number = normalizedPhone;

                      // Handle name column (optional)
                      const nameRaw = hasNameColumn ? row.name?.toString().trim() : null;
                      if (nameRaw && nameRaw.length > 100) {
                           validationErrors.push(`Row ${lineNumber}: Name starting with '${nameRaw.substring(0,20)}...' is too long (max 100 characters) and has been truncated.`);
                           lead.name = nameRaw.substring(0, 100);
                      } else if (nameRaw) {
                           lead.name = nameRaw;
                      } else {
                           lead.name = null;
                      }

                      // Handle email column (optional, only if pre-call emails enabled)
                      if (isPreCallEmailsEnabled && hasEmailColumn) {
                          const emailRaw = row.email?.toString().trim();
                          if (emailRaw) {
                              // Basic email validation
                              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                              if (emailRegex.test(emailRaw)) {
                                  lead.email = emailRaw.toLowerCase();
                              } else {
                                  validationErrors.push(`Row ${lineNumber}: Invalid email format '${emailRaw}'. Email skipped.`);
                                  lead.email = null;
                              }
                          } else {
                              lead.email = null;
                          }
                      } else {
                          lead.email = null;
                      }
                      
                      validLeads.push(lead as ManualLead);
                  });
              }

              results.errors.forEach((err: any) => { 
                   const rowInfo = err.row !== undefined ? `near Row ${err.row + 2}` : '';
                   validationErrors.push(`CSV File Structure Error ${rowInfo}: ${err.message} (Code: ${err.code}).`);
              });

              setParseErrors(validationErrors);
              setParsedLeads(validLeads);
              
              toast.dismiss(parseToastId);

              // Enhanced success/error messaging
              if (validLeads.length === 0 && validationErrors.length === 0 && results.data.length > 0 && headers && headers.includes('phone_number')) {
                   toast.warning(`CSV parsed successfully, but no valid leads were extracted from ${results.data.length} data rows. Please check phone formats and ensure names are not excessively long.`, { duration: 8000 });
              } else if (validLeads.length > 0 && validationErrors.length === 0) {
                  toast.success(`${validLeads.length} valid lead(s) parsed from ${currentFileName}. Ready to submit.`);
              } else if (validLeads.length > 0 && validationErrors.length > 0) {
                  const errorCount = validationErrors.filter(err => !err.includes('info:') && !err.includes('warning:')).length;
                  const warningCount = validationErrors.filter(err => err.includes('warning:')).length;
                  const infoCount = validationErrors.filter(err => err.includes('info:')).length;
                  
                  let message = `${validLeads.length} valid lead(s) parsed`;
                  if (errorCount > 0) message += ` with ${errorCount} error(s)`;
                  if (warningCount > 0) message += ` and ${warningCount} warning(s)`;
                  if (infoCount > 0) message += ` (${infoCount} info note(s))`;
                  message += `. Invalid rows were skipped. Review details below.`;
                  
                  toast.warning(message, { duration: 8000 });
              } else if (validationErrors.length > 0) { 
                  toast.error(`Errors found processing CSV '${currentFileName}'. No valid leads could be extracted. Please review the errors below and correct the file.`, { duration: 8000 });
              } else if (results.data.length === 0 && (!headers || !headers.includes('phone_number'))) {
                  toast.error(`The CSV file '${currentFileName}' structure is incorrect or the critical 'phone_number' header is missing.`, { duration: 8000 });
              } else if (results.data.length === 0) { 
                  toast.warning(`The selected CSV file '${currentFileName}' appears to be empty or contains only headers. No leads to process.`, { duration: 6000 });
              }
              setIsSubmitting(false); 
          },
          error: (parserError: Error, fileOnError?: LocalFile) => { // Reverted to Error type for the first parameter
              console.error("CSV Parsing Failed (Papa.parse error callback):", parserError, fileOnError);
              toast.dismiss(parseToastId);
              
              let errorMessage = `CSV File Read Error: ${parserError.message}`;
              // PapaParse errors often have a 'code' property, even if the base 'Error' type doesn't guarantee it.
              // We use a type guard to safely access it.
              if ('code' in parserError && typeof (parserError as any).code !== 'undefined') {
                   errorMessage += ` (Code: ${(parserError as any).code})`;
              }

              setParseErrors((prev:string[]) => [...prev, errorMessage]);
              setIsSubmitting(false); 
          }
      });
  } catch (err: any) {
      console.error("Critical error during Papa.parse setup or synchronous file processing:", err);
      toast.dismiss(parseToastId);
      toast.error(`Critical file processing error: ${err.message}. Please ensure the file is a valid CSV and try again.`);
      setParseErrors((prev:string[]) => [...prev, `Critical error during file processing: ${err.message}`]);
      setIsSubmitting(false); 
  }
};

export const validateInstruction = (manualCallInstruction: string): boolean => {
  const trimmedInstruction = manualCallInstruction.trim();
  if (trimmedInstruction.length < INSTRUCTION_MIN_LENGTH) {
      toast.error(`Instruction too short. Please provide a clear instruction (min ${INSTRUCTION_MIN_LENGTH} characters).`);
      return false;
  }
  if (trimmedInstruction.length > INSTRUCTION_MAX_LENGTH) {
      toast.error(`Instruction too long (max ${INSTRUCTION_MAX_LENGTH} characters).`);
      return false;
  }
  return true;
};

export const submitLeads = async (
  leads: ManualLead[],
  user: User | null,
  setIsSubmitting: (isSubmitting: boolean) => void,
  handleModalOpenChange: (isOpen: boolean) => void,
  businessName: string | null,
  isPreCallEmailsEnabled: boolean,
  manualCallInstruction: string,
  supabase: SupabaseClient
) => {
  if (!user) {
      toast.error('Authentication error. Please log in again.');
      return;
  }
  if (leads.length === 0) {
      toast.warning('No valid leads to submit.');
      return;
  }
  if (!validateInstruction(manualCallInstruction)) { 
      return;
  }

  setIsSubmitting(true); 
  const submissionToastId = toast.loading(`Submitting ${leads.length} lead(s)...`);
  const finalInstruction = manualCallInstruction.trim();

  const leadsToInsert = leads.map(lead => ({
      user_id: user.id,
      customer_name: lead.name || null,
      customer_phone: lead.phone_number, // Already normalized to E.164
      customer_email: lead.email || null, // Email for pre-call notifications
      call_type: 'manual_upload' as const, 
      status: 'pending' as const,
      attempts: 0,
      calendar_integration_id: null,
      calendar_event_id: null,
      event_start_time: null,
      event_end_time: null,
      event_summary: null, 
      manual_upload_instruction: finalInstruction, 
      last_attempt_time: null,
      last_error: null,
      vapi_call_id: null,
  }));

  console.log("Attempting to insert into outbound_call_queue:", leadsToInsert);

  try {
      const { data: callRecord, error } = await supabase
          .from('outbound_call_queue')
          .insert(leadsToInsert)
          .select()
          .single();


      if (error) {
          console.error('Supabase insert error into outbound_call_queue:', error);
          if (error.message.includes('violates row-level security policy')) {
              throw new Error(`Permission denied. Please check RLS policies for outbound_call_queue.`);
          } else if (error.message.includes('violates not-null constraint')) {
               throw new Error(`Database Error: A required field is missing. Details: ${error.details}`);
          } else {
              throw new Error(`Database error: ${error.message}`);
          }
      }

      // Send pre-call emails to leads with email addresses
      try {
          await sendPreCallEmails(leadsToInsert, finalInstruction, businessName || 'Our Team', user.id, isPreCallEmailsEnabled, callRecord?.id);
      } catch (emailError) {
          console.error('Error sending pre-call emails:', emailError);
      }

      const payload = new URLSearchParams({
      user_email: user.email ?? '',
      total_leads: leads.length.toString(),
      instruction: finalInstruction,
      timestamp: new Date().toISOString(),
      });

      await fetch(ZAPIER_SLACK_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: payload.toString(),
      });


      toast.success(`${leads.length} lead(s)  added to the call queue!`, { id: submissionToastId });
      handleModalOpenChange(false); 

  } catch (error: any) {
      console.error("Lead submission failed:", error);
      
      // Check if this is a delinquent account error
      if (error.message && error.message.includes('is delinquent')) {
          toast.error(
              "Account Suspended: You need credits to add leads to the call queue. Purchase a boost pack to restore service and continue.", 
              { 
                  id: submissionToastId,
                  duration: 8000, // Show longer for important account status
                  action: {
                      label: "Add Credits",
                      onClick: () => {
                          window.location.href = '/dashboard/billing?tab=boost-packs';
                      }
                  }
              }
          );
      } else {
          // Handle other types of errors with original logic
          toast.error(`Failed to add leads: ${error.message}`, { id: submissionToastId });
      }
      setIsSubmitting(false); 
  }
};

export const downloadTemplate = (isPreCallEmailsEnabled: boolean) => {
  const csvContent = isPreCallEmailsEnabled 
      ? "data:text/csv;charset=utf-8,name,phone_number,email\nExample Name,(555) 123-4567,example@email.com\nAnother Lead,18889990000,another@email.com\nNo Name Lead,+12223334444,\nJohn Doe,+1 (555) 987-6543,john.doe@example.com"
      : "data:text/csv;charset=utf-8,name,phone_number\nExample Name,(555) 123-4567\nAnother Lead,18889990000\nNo Name Lead,+12223334444\nJohn Doe,+1 (555) 987-6543";
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "lead_upload_template.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  toast.info("CSV template download started.");
};